// 9fbef606107a605d69c0edbcd8029e5d
import React from 'react';
import isEmpty from 'lodash/isEmpty';
import { PropTypes } from 'prop-types';
import { withRouter } from 'next/router'; //eslint-disable-line
import hoistNonReactStatic from 'hoist-non-react-statics';
import { fetchPageLayout, loadPageSEOData } from '@tcp/core/src/reduxStore/actions';
import { createLayoutPath, getAPIConfig, isGymboree } from '@tcp/core/src/utils';
import GetCandidGallery from '@tcp/core/src/components/common/molecules/GetCandidGallery/views/GetCandidGallery';
import StoresInternational from '@tcp/core/src/components/features/storeLocator/StoresInternational/container';
import DynamicSizeChart from '@tcp/core/src/components/common/molecules/DynamicSizeChart';
import { getIsDynamicSizeChartEnabled } from '@tcp/core/src/reduxStore/selectors/session.selectors';
import Static from '../components/features/content/Static';
import STATIC_PAGE_CONSTANTS from '../components/features/content/Static/Static.constants';
import constants from '../constants';

const pathPrefix = STATIC_PAGE_CONSTANTS.STATIC_PAGE_PREFIX;
const tcpRouteName = [
  'girls-size-chart',
  'boys-size-chart',
  'toddler-size-chart',
  'baby-size-chart',
];
const gymRouteName = [
  'toddler-girl-size-chart',
  'toddler-boy-size-chart',
  'toddler-shoe-size-chart',
];

/**
 * Retrieves the department name based on the given content type.
 *
 * This function determines the department name by matching the provided `contentType`
 * with predefined size chart routes. It supports two scenarios:
 * 1. When `contentType` is a direct match (e.g., "girls-size-chart").
 * 2. When `contentType` has a prefix before "/" (e.g., "size-chart/girls-size-chart"),
 *    in which case it extracts the part after "/" and then performs the match.
 *
 * @param {string} contentType - The content type to match against the size chart routes.
 * @returns {string | null} - The extracted department name or null if no match is found.
 */
export const getDepartmentVal = (contentType, fromPage, isServer) => {
  let sanitizedContentType;

  if (isServer && !isEmpty(fromPage)) {
    sanitizedContentType = fromPage;
  }
  if (!isServer && !isEmpty(contentType)) {
    sanitizedContentType = contentType.includes('/') ? contentType.split('/').pop() : contentType;
  }

  const routeNames = isGymboree() ? gymRouteName : tcpRouteName;
  const matchedRoute = routeNames.find((route) => sanitizedContentType === route);

  // Remove '-size-chart' from the matched route to return the department name
  return matchedRoute ? matchedRoute.replace('-size-chart', '') : null;
};

class Content extends React.Component {
  static async getInitialProps({ store, isServer, query, res = {} }) {
    const { contentType: urlPath, fromPage } = query;
    const isDynamicSizeChartEnabled = getIsDynamicSizeChartEnabled(store.getState());
    let apiConfig;
    if (isServer) {
      const {
        locals: { apiConfig: config },
      } = res;
      apiConfig = config;
    } else {
      apiConfig = getAPIConfig();
    }
    store.dispatch(loadPageSEOData({ page: `/${pathPrefix}${urlPath}`, apiConfig }));
    const formattedUrlPath = createLayoutPath(urlPath);
    if (!isServer && urlPath) {
      const state = store.getState();
      if (
        !constants.staticPagesWithOwnTemplate.includes(urlPath) &&
        !state.Layouts[formattedUrlPath]
      ) {
        store.dispatch(fetchPageLayout({ pageName: `${pathPrefix}${urlPath}`, apiConfig }));
      }
    }
    return { urlPath, fromPage, isDynamicSizeChartEnabled, isServer };
  }

  render() {
    const {
      urlPath,
      setResStatusNotFound,
      fromPage,
      isDynamicSizeChartEnabled,
      router: { query: { contentType = '' } = {} } = {},
      isServer,
    } = this.props;
    const isExactMatch = contentType.includes('size-chart');
    const sizeChartDepartmentVal = getDepartmentVal(contentType, fromPage, isServer);

    let contentComponent;

    if (isExactMatch && isDynamicSizeChartEnabled) {
      return <DynamicSizeChart activeSizeChart={sizeChartDepartmentVal} isContentPage />;
    }

    switch (urlPath) {
      case 'mystyleplace':
        contentComponent = <GetCandidGallery />;
        break;
      case 'international-stores':
        contentComponent = <StoresInternational />;
        break;
      default:
        contentComponent = (
          <Static
            urlPath={urlPath}
            setResStatusNotFound={setResStatusNotFound}
            fromPage={fromPage}
          />
        );
    }
    return contentComponent;
  }
}

Content.pageInfo = {
  pageId: 'content',
  staticPage: true,
  pathPrefix,
  paramName: 'contentType',
};

Content.propTypes = {
  urlPath: PropTypes.string.isRequired,
  setResStatusNotFound: PropTypes.func.isRequired,
  isDynamicSizeChartEnabled: PropTypes.bool,
};

Content.defaultProps = {
  isDynamicSizeChartEnabled: false,
};

const ContentWithRouter = withRouter(Content);

hoistNonReactStatic(ContentWithRouter, Content, {
  getInitialProps: true,
});

export default ContentWithRouter;
